import { useState } from "react";
import { api } from "../lib/api";
import { useResource, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { InitialsAvatar } from "../components/InitialsAvatar";

interface UserRow {
    id: string;
    email: string;
    full_name: string;
    role: string;
    is_active: boolean;
    last_login_at: string | null;
}

interface ResetLink {
    userId: string;
    fullName: string;
    email: string;
    token: string;
    expires_at: string;
}

/**
 * The whole of the IT support account's job: find a person's account, reset
 * their password or reactivate a locked-out login. Nothing here touches
 * participants, clinical records, rosters or money — that data is refused by
 * the API for this role regardless of what this page shows, but the page
 * itself carries no link toward any of it either.
 */
export const Support = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const users = useResource<UserRow[]>("/auth/users");

    const [busyId, setBusyId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [resetLink, setResetLink] = useState<ResetLink | null>(null);

    // The API emails the one-time link straight to the registered address —
    // it never reaches this page, so there is nothing here to copy or leak.
    const resetPassword = async (row: UserRow) => {
        setBusyId(row.id);
        setError(null);
        try {
            const sent = await api.post<{ sent: boolean; email: string; expires_at: string }>(
                `/auth/users/${row.id}/reset-password`, {});
            setResetLink({ userId: row.id, fullName: row.full_name, email: sent.email,
                token: "", expires_at: sent.expires_at });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not send the reset link");
        } finally {
            setBusyId(null);
        }
    };

    const setActive = async (row: UserRow, active: boolean) => {
        setBusyId(row.id);
        setError(null);
        try {
            await api.put(`/auth/users/${row.id}/active`, { active });
            users.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not update the account");
        } finally {
            setBusyId(null);
        }
    };

    if (users.error) return <div className="error-banner">{users.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1.1rem" }}>
            {error && <div className="error-banner">{error}</div>}

            {resetLink && (
                <div className="card">
                    <div className="card-head">
                        <div>
                            <h2>Reset link emailed — {resetLink.fullName}</h2>
                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                                Sent automatically to their registered address <b>{resetLink.email}</b>.
                                It works once and expires {formatDateTime(resetLink.expires_at)}. The link is
                                never shown to anyone — you included. If it expires or doesn't arrive,
                                click Reset password again to send a fresh one (the old link stops working).
                            </p>
                        </div>
                        <button className="btn ghost small" onClick={() => setResetLink(null)}>Close</button>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="card-head">
                    <h2>Accounts</h2>
                </div>
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th><th>Role</th><th>Status</th>
                                    <th>Last sign-in</th><th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(users.data ?? []).map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                <InitialsAvatar name={row.full_name} size={30} />
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{row.full_name}</div>
                                                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{row.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="chip info">{titleCase(row.role)}</span></td>
                                        <td>
                                            {row.is_active
                                                ? <span className="chip pass">Active</span>
                                                : <span className="chip block">Deactivated</span>}
                                        </td>
                                        <td className="mono">
                                            {row.last_login_at ? formatDateTime(row.last_login_at) : "never"}
                                        </td>
                                        <td>
                                            <div className="row-actions">
                                                {!row.is_active && (
                                                    <button className="icon-btn" disabled={busyId === row.id}
                                                        onClick={() => void setActive(row, true)}>
                                                        Reactivate
                                                    </button>
                                                )}
                                                {row.is_active && (
                                                    <button className="icon-btn" disabled={busyId === row.id}
                                                        onClick={() => void resetPassword(row)}>
                                                        Reset password
                                                    </button>
                                                )}
                                                {isAdmin && row.is_active && (
                                                    <button className="icon-btn danger" disabled={busyId === row.id}
                                                        onClick={() => void setActive(row, false)}>
                                                        Deactivate
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {(users.data ?? []).length === 0 && (
                                    <tr><td colSpan={5} className="empty">No accounts.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
